import { Component, inject, input, output, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import {
  CustomerCouponsApi,
  CustomerCouponAvailability,
} from '../../services/customer-coupons-api';
import { AvailableCoupon } from '../../services/cart-api';
import { AuthService } from '../../services/auth.service';

export type CouponType = AvailableCoupon | CustomerCouponAvailability;

@Component({
  selector: 'app-shop-voucher-modal',
  templateUrl: './shop-voucher-modal.html',
  styleUrl: './shop-voucher-modal.css',
  imports: [TranslateModule, FormsModule],
})
export class ShopVoucherModal {
  private customerCouponsApi = inject(CustomerCouponsApi);
  private authService = inject(AuthService);
  private router = inject(Router);
  isModalOpen = input(false);
  sellerStoreId = input.required<string>();
  sellerSubtotal = input.required<number>();
  currentSelectedCouponId = input<string | null>(null);
  currentSelectedCouponCode = input<string | null>(null);
  availableCoupons = input<CouponType[]>([]);

  couponSelected = output<CouponType>();
  closed = output<void>();

  manualCode = signal('');
  validationError = signal<string | null>(null);
  isValidating = signal(false);

  onCloseModal() {
    this.closed.emit();
    this.manualCode.set('');
    this.validationError.set(null);
  }

  onApplyManualCode() {
    const code = this.manualCode().trim();
    if (!code) {
      this.validationError.set('Please enter a voucher code');
      return;
    }

    if (!this.authService.isLoggedIn()) {
      this.onCloseModal();
      void this.router.navigate(['/auth/login'], {
        queryParams: { returnUrl: this.router.url },
      });
      return;
    }

    this.isValidating.set(true);
    this.validationError.set(null);

    if (this.currentSelectedCouponId()) {
      this.validationError.set('You already have a coupon selected');
      this.isValidating.set(false);
      return;
    }

    this.customerCouponsApi
      .validateCoupon({
        sellerStoreId: this.sellerStoreId(),
        sellerSubtotal: this.sellerSubtotal(),
        code,
        currentSelectedCouponId: this.currentSelectedCouponId(),
        currentSelectedCouponCode: this.currentSelectedCouponCode(),
      })
      .subscribe({
        next: (coupon) => {
          this.isValidating.set(false);
          this.couponSelected.emit(coupon);
          this.manualCode.set('');
        },
        error: (err) => {
          this.isValidating.set(false);
          const message = err.error?.error.message || 'Invalid voucher code';
          this.validationError.set(message);
        },
      });
  }

  onSelectCoupon(coupon: CouponType) {
    this.manualCode.set(coupon.code);
  }

  formatDiscount(coupon: CouponType): string {
    if (coupon.discountType === 'Percentage') {
      return `-${coupon.discountValue}%`;
    } else {
      const currency = coupon.discountValueCurrency || '';
      return `-${coupon.discountValue.toFixed(2)} ${currency}`;
    }
  }
}
